import AssociationLibraryCst from "@components/AssociationLibraryCst";
import { Dialog, DialogContent, GlobalStyles } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ServicesURL } from "@utils/utilsApi";
import { t } from "i18next";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { MediaType } from "src/interfaces/Media";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import allPlugins from "suneditor/src/plugins";

interface CustomSunEditorProps {
  value: string;
  onChange: (content: string) => void;
  getSunEditorInstance?: (editor: any) => void;
  isPortrait?: boolean;
  isForNotification?: boolean;
}
const CustomSunEditor: React.FC<CustomSunEditorProps> = ({
  value,
  onChange,
  getSunEditorInstance,
  isPortrait = true,
  isForNotification = false,
}) => {
  const theme = useTheme();

  const { id = "1" } = useParams();
  const [fileAssociation, setFileAssociation] = useState<boolean | undefined>(
    undefined,
  );
  const [editorObject, setEditorObject] = useState<any>(null);

  let htmlRef = undefined as any;

  const mediaLibraryImagePlugin = {
    name: "mediaLibraryImage",
    display: "command",
    title: "Image from Media Library",
    innerHTML: '<i class="se-icon-text">+</i>',

    add: function (core: any, targetElement: any) {
      core.context.mediaLibraryImagePlugin = {
        targetButton: targetElement,
      };
    },

    action: function (this: any) {
      htmlRef = this;
      setEditorObject(this);
      setFileAssociation(true);
    },
  };

  const handleLibraryFile = async (associatedFile: any) => {
    const response = await fetcher(
      `${ServicesURL.getMediaManagerFile}?mediaId=${associatedFile[0].id}&corporateId=${id}&generateUrl=true`,
      MethodType.GET,
      undefined,
    );

    const url = response?.output[0]?.url?.public_url;
    if (!url) {
      console.error("downloadError: url not found");
      return;
    }

    const originalFileName = response?.output[0]?.metadata?.originalFileName;
    if (!originalFileName) {
      console.error("downloadError: originalFileName not found");
      return;
    }

    const downloadResponse = await fetch(url);
    if (!downloadResponse.ok) {
      console.error("downloadError: failed to fetch file");
      return;
    }

    const blob = await downloadResponse.blob();
    const file = new File([blob], associatedFile[0].label, { type: blob.type });

    editorObject?.functions?.insertImage([file], false);
  };

  const previewTemplateForNotification = isForNotification
    ? `
  <div class="preview" style="margin-left: auto; margin-right: auto; width: 600px; border: dashed 1px; padding: inherit;">
	  {{contents}}
  </div>
  `
    : "";

  const pageWidth = isPortrait ? "210mm" : "297mm";
  const pageHeight = isPortrait ? "297mm" : "210mm";
  const pageLimit = isPortrait ? "245mm" : "175mm";

  const sunEditorStyles = {
    ".sun-editor .se-wrapper .se-wrapper-code": {
      height: isPortrait ? "297mm" : "210mm",
      minHeight: isPortrait ? "297mm" : "210mm",
    },
    ".sun-editor .se-wrapper .se-wrapper-wysiwyg": {
      width: isPortrait ? "210mm" : "297mm",
      maxWidth: "100%",

      minHeight: pageHeight,
      height: "auto",
      maxHeight: "none",

      margin: "auto",

      backgroundColor: "#fff",
      overflow: "visible",
    },
    ".sun-editor .se-wrapper .se-wrapper-wysiwyg::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      backgroundImage: `repeating-linear-gradient(
    to bottom,
    transparent 0,
    transparent calc(${pageLimit} - 2px),
    rgba(255, 0, 0, 0.75) calc(${pageLimit} - 2px),
    rgba(255, 0, 0, 0.75) ${pageLimit}
  )`,
      WebkitMaskImage: `repeating-linear-gradient(
    to right,
    #000 0, #000 8px,
    transparent 8px, transparent 14px
  )`,
      maskImage: `repeating-linear-gradient(
    to right,
    #000 0, #000 8px,
    transparent 8px, transparent 14px
  )`,
    },

    ".sun-editor .se-toolbar": {
      backgroundColor: theme.palette.background.paper,
    },
    ".sun-editor .se-tooltip": {
      color: theme.palette.text.primary,
    },
    ".sun-editor .se-dialog .se-dialog-inner": {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -25%)",
    },
    ".sun-editor .se-controller-resizing": {
      backgroundColor: theme.palette.background.paper,
    },
    ".sun-editor .se-list-layer": {
      backgroundColor: theme.palette.background.paper,
    },
    ".sun-editor .se-controller .se-btn-group button": {
      backgroundColor: theme.palette.background.paper,
    },
    ".sun-editor button": {
      color: theme.palette.text.primary,
    },
  };
  return (
    <>
      <GlobalStyles styles={sunEditorStyles} />
      <SunEditor
        getSunEditorInstance={getSunEditorInstance}
        setContents={value}
        onChange={onChange}
        setOptions={{
          plugins: [...Object.values(allPlugins), mediaLibraryImagePlugin],
          buttonList: [
            ["undo", "redo"],
            ["font", "fontSize"],
            ["paragraphStyle", "blockquote"],
            [
              "bold",
              "underline",
              "italic",
              "strike",
              "subscript",
              "superscript",
            ],
            ["fontColor", "hiliteColor"],
            ["align", "list", "lineHeight"],
            ["outdent", "indent"],
            [
              "table",
              "horizontalRule",
              "link",
              "image",
              "mediaLibraryImage",
              "video",
            ],
            // ['math'] //You must add the 'katex' library at options to use the 'math' plugin.
            // ["imageGallery"], // You must add the "imageGalleryUrl".
            ["showBlocks", "codeView"],
            //["preview", "print"],
            ["removeFormat"],
            isForNotification ? ["preview"] : [],
            // ['save', 'template'],
            // '/', Line break
          ], // Or Array of button list, eg. [['font', 'align'], ['image']]
          defaultTag: "div",
          minWidth: isForNotification ? "auto" : pageWidth,
          minHeight: pageHeight,
          height: "auto",
          maxHeight: "none",
          showPathLabel: false,
          attributesWhitelist: {
            all: "style|data-align|data-percentage|data-size|data-file-name|data-origin|data-index|data-rotate|data-proportion|data-file-size",
            table: "width|height|align|cellpadding|cellspacing|border",
            input: "checked",
          },
          resizeEnable: false,
          linkNoPrefix: true,
          charCounter: isForNotification,
          strictHTMLValidation: isForNotification ? false : true,
          previewTemplate: previewTemplateForNotification,
        }}
        width={isForNotification ? "auto" : pageWidth}
        height="auto"
      />
      {fileAssociation && (
        <Dialog open={fileAssociation} maxWidth={"xl"}>
          <DialogContent>
            <AssociationLibraryCst
              association={undefined}
              title={t("image-association", { ns: "service" })}
              filters={[{ field: "type", value: MediaType.OTHER }]}
              setAssociation={handleLibraryFile}
              setCloseAssociation={setFileAssociation}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default CustomSunEditor;
